import React, { useEffect, useRef, useState } from 'react';
import { Copy, X } from 'lucide-react';
import './OverflowTip.css';

// Works on every admin page without changing the pages: when a text is cut off with "…", hovering it shows a pointer, and
// clicking it opens a small card with the full text (and a Copy button). Click outside, press Esc or scroll to close it.
const NOT_FOR = 'button, a, input, select, textarea, label, [role="button"], [role="menuitem"], [contenteditable="true"], .admin-sidebar, .admin-sidebar *';

// the element (the one under the pointer, or one of its few parents) whose text is cut off, or null
const truncatedElement = (start) => {
    let el = start;
    for (let depth = 0; el && el !== document.body && depth < 4; depth += 1, el = el.parentElement) {
        const style = window.getComputedStyle(el);
        const cutHorizontally = style.overflowX !== 'visible' && style.textOverflow === 'ellipsis' && el.scrollWidth > el.clientWidth + 1;
        const clamp = parseInt(style.webkitLineClamp, 10);
        const cutLines = clamp > 0 && style.overflowY !== 'visible' && el.scrollHeight > el.clientHeight + 1;
        if (cutHorizontally || cutLines) return el;
    }
    return null;
};

const OverflowTip = () => {
    const [tip, setTip] = useState(null); // { text, top, left, width }
    const cardRef = useRef(null);
    const hovered = useRef(null);

    useEffect(() => {
        const onOver = (event) => {
            if (event.target.closest?.(NOT_FOR) || event.target.closest?.('.overflow-tip')) return;
            const el = truncatedElement(event.target);
            if (hovered.current && hovered.current !== el) hovered.current.classList.remove('has-overflow-tip');
            hovered.current = el;
            if (el) el.classList.add('has-overflow-tip');
        };

        const onClick = (event) => {
            if (event.target.closest?.('.overflow-tip')) return; // clicks inside the card are its own
            if (event.target.closest?.(NOT_FOR)) { setTip(null); return; }
            const el = truncatedElement(event.target);
            const text = el ? (el.innerText || el.textContent || '').trim() : '';
            if (!el || !text) { setTip(null); return; }
            const rect = el.getBoundingClientRect();
            const width = Math.min(360, window.innerWidth - 24);
            const left = Math.min(Math.max(12, rect.left), window.innerWidth - width - 12);
            // below the text; above it when there is no room underneath
            const below = rect.bottom + 8;
            const top = below + 120 > window.innerHeight ? Math.max(12, rect.top - 8) : below;
            setTip({ text, left, top, width, above: top < rect.top });
        };

        const close = () => setTip(null);
        const onKey = (event) => { if (event.key === 'Escape') close(); };

        document.addEventListener('mouseover', onOver, true);
        document.addEventListener('click', onClick, true);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mouseover', onOver, true);
            document.removeEventListener('click', onClick, true);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, []);

    if (!tip) return null;

    const copy = async () => {
        try { await navigator.clipboard.writeText(tip.text); } catch { /* clipboard blocked: the text can still be selected */ }
    };

    return (
        <div ref={cardRef} className="overflow-tip" role="dialog" aria-label="Full text"
            style={{ left: tip.left, top: tip.top, width: tip.width, transform: tip.above ? 'translateY(-100%)' : 'none' }}>
            <div className="overflow-tip__head">
                <span>Full text</span>
                <span>
                    <button type="button" onClick={copy} title="Copy"><Copy size={13} /></button>
                    <button type="button" onClick={() => setTip(null)} title="Close"><X size={14} /></button>
                </span>
            </div>
            <p>{tip.text}</p>
        </div>
    );
};

export default OverflowTip;
